'use client';

import ControlledButton from '@umodeapporg/ui/components/Form/ControlledButton';
import ControlledInput from '@umodeapporg/ui/components/Form/ControlledInput';
import CustomBadge from '@umodeapporg/ui/components/UI/CustomBadge';
import LoadingSpinner from '@umodeapporg/ui/components/UI/LoadingSpinner';
import { MaterialSymbol } from '@umodeapporg/ui/components/UI/MaterialSymbol';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAgente } from '@/domain/agente/hooks/useAgente';
import type { FilaItem } from '@/domain/fila/interfaces/fila';
import { logger } from '@/infrastructure/persistence/managers/LoggerManager';

import { buildCopilotoSugestoes } from './homeCopiloto';

interface IHomeCopilotoBoxProps {
  /** The day's queue, which is where the suggestions get the OP and the cell to name. */
  itens: Array<FilaItem>;
}

/**
 * The way into the Copiloto, in the HEADER of the home: the question is typed here and
 * the answer opens in the side panel that every other screen already uses, so there is
 * only ever one conversation.
 *
 * A proposal waiting for a decision would be invisible on a strip, so it comes out on the
 * badge, the way the floating launcher carries it on the other screens.
 */
export function HomeCopilotoBox({ itens }: IHomeCopilotoBoxProps) {
  const { t } = useTranslation('common');
  const { abrirPainel, enviar, conversa, loading, contextoAtual } = useAgente();
  const [pergunta, setPergunta] = useState('');

  const sugestoes = useMemo(() => buildCopilotoSugestoes(t, itens), [t, itens]);
  const pendentes = conversa.propostasPendentes.length;
  const podePerguntar = pergunta.trim() !== '' && !loading;

  const perguntar = async (texto: string): Promise<void> => {
    const limpo = texto.trim();
    if (limpo === '' || loading) {
      return;
    }
    // Open FIRST: the panel is where the answer lands, and waiting for the round trip
    // with the home still on screen reads as nothing having happened.
    abrirPainel();
    setPergunta('');
    try {
      await enviar(limpo, contextoAtual());
    } catch (error) {
      logger.error('[HomeCopilotoBox] failed to send question', error);
    }
  };

  return (
    <section className="flex w-full flex-col gap-2 laptop:w-[min(55vw,40rem)]">
      <div className="flex items-center gap-2">
        <MaterialSymbol icon="smart_toy" size={20} className="shrink-0 text-brand-emphasis" />
        <ControlledInput
          label={t('home.copiloto.chamada')}
          value={pergunta}
          containerClassName="min-w-0 flex-1"
          disabled={loading}
          onChange={(event) => setPergunta(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              perguntar(pergunta);
            }
          }}
        />
        <CustomBadge content={pendentes} invisible={pendentes === 0} tone="danger" className="ring-2 ring-surface">
          <ControlledButton
            type="button"
            size="sm"
            className="flex shrink-0 items-center gap-x-1"
            disabled={!podePerguntar}
            onClick={() => perguntar(pergunta)}
          >
            {loading ? <LoadingSpinner size={4} color="brand-fg" /> : <MaterialSymbol icon="send" size={16} />}
            {t('home.copiloto.perguntar')}
          </ControlledButton>
        </CustomBadge>
      </div>

      {/* Tapping fills the box and asks at once: the person reads the question, not types it.
          `normal-case` because these are SENTENCES: the button's own capitalize would turn
          "O que está atrasado hoje?" into a title. */}
      <div className="flex flex-wrap gap-1.5">
        {sugestoes.map((sugestao) => (
          <ControlledButton
            key={sugestao.id}
            type="button"
            variant="outlined"
            tone="neutral"
            size="sm"
            className="normal-case"
            disabled={loading}
            onClick={() => perguntar(sugestao.texto)}
          >
            {sugestao.texto}
          </ControlledButton>
        ))}
      </div>
    </section>
  );
}
